import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, FileText, Receipt, FileQuestion, Save, Star } from 'lucide-react';

const BillingSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);

  const [bankForm, setBankForm] = useState({
    account_name: '',
    account_type: 'Current',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',
    is_primary: false
  });

  const [terms, setTerms] = useState({
    invoice_terms: ['', '', '', '', ''],
    estimation_terms: ['', '', '', '', ''],
    cash_receipt_terms: ['', '', '', '', '']
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/billing-settings/');
      setSettings(response.data);
      
      // Initialize terms from settings
      const invoiceTerms = response.data.invoice_terms || [];
      const estimationTerms = response.data.estimation_terms || [];
      const cashReceiptTerms = response.data.cash_receipt_terms || [];
      
      setTerms({
        invoice_terms: [...invoiceTerms, ...Array(5 - invoiceTerms.length).fill('')].slice(0, 5),
        estimation_terms: [...estimationTerms, ...Array(5 - estimationTerms.length).fill('')].slice(0, 5),
        cash_receipt_terms: [...cashReceiptTerms, ...Array(5 - cashReceiptTerms.length).fill('')].slice(0, 5)
      });
    } catch (error) {
      toast.error('Failed to load billing settings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddBank = () => {
    setEditingBank(null);
    setBankForm({
      account_name: '',
      account_type: 'Current',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      branch_name: '',
      is_primary: settings?.bank_accounts?.length === 0
    });
    setBankDialogOpen(true);
  };

  const handleOpenEditBank = (account) => {
    setEditingBank(account);
    setBankForm({
      account_name: account.account_name,
      account_type: account.account_type,
      bank_name: account.bank_name,
      account_number: account.account_number,
      ifsc_code: account.ifsc_code,
      branch_name: account.branch_name,
      is_primary: account.is_primary
    });
    setBankDialogOpen(true);
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    
    if (!bankForm.account_name || !bankForm.bank_name || !bankForm.account_number || !bankForm.ifsc_code) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingBank) {
        await api.put(`/billing-settings/bank-accounts/${editingBank.id}`, bankForm);
        toast.success('Bank account updated');
      } else {
        await api.post('/billing-settings/bank-accounts', bankForm);
        toast.success('Bank account added');
      }
      setBankDialogOpen(false);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBank = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
    try {
      await api.delete(`/billing-settings/bank-accounts/${accountId}`);
      toast.success('Bank account deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete bank account');
    }
  };

  const handleTermChange = (type, index, value) => {
    setTerms(prev => ({
      ...prev,
      [type]: prev[type].map((t, i) => i === index ? value : t)
    }));
  };

  const handleSaveTerms = async () => {
    setSubmitting(true);
    try {
      // Filter out empty terms
      const payload = {
        invoice_terms: terms.invoice_terms.filter(t => t.trim()),
        estimation_terms: terms.estimation_terms.filter(t => t.trim()),
        cash_receipt_terms: terms.cash_receipt_terms.filter(t => t.trim())
      };
      
      await api.put('/billing-settings/terms', payload);
      toast.success('Billing terms saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save terms');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing Settings</h1>
        <p className="text-gray-600">Manage bank accounts and billing terms for invoices</p>
      </div>

      <Tabs defaultValue="bank">
        <TabsList>
          <TabsTrigger value="bank">
            <Building2 className="h-4 w-4 mr-2" />
            Bank Accounts
          </TabsTrigger>
          <TabsTrigger value="terms">
            <FileText className="h-4 w-4 mr-2" />
            Billing Terms
          </TabsTrigger>
        </TabsList>

        {/* Bank Accounts Tab */}
        <TabsContent value="bank" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Bank Accounts</CardTitle>
                  <CardDescription>Bank details shown on invoices for payment</CardDescription>
                </div>
                <Button onClick={handleOpenAddBank}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Bank Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {settings?.bank_accounts?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account Name</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead>Account Number</TableHead>
                      <TableHead>IFSC</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {settings.bank_accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium">{account.account_name}</TableCell>
                        <TableCell>
                          <div>
                            <p>{account.bank_name}</p>
                            <p className="text-xs text-gray-500">{account.branch_name}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">{account.account_number}</TableCell>
                        <TableCell className="font-mono">{account.ifsc_code}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{account.account_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {account.is_primary && (
                            <Badge className="bg-green-100 text-green-800">
                              <Star className="h-3 w-3 mr-1" />
                              Primary
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEditBank(account)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteBank(account.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No bank accounts added yet</p>
                  <Button variant="link" onClick={handleOpenAddBank}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add your first bank account
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Terms Tab */}
        <TabsContent value="terms" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Billing Terms</CardTitle>
                  <CardDescription>Terms & conditions printed on invoices, receipts, and quotations (max 5 lines each)</CardDescription>
                </div>
                <Button onClick={handleSaveTerms} disabled={submitting}>
                  <Save className="h-4 w-4 mr-2" />
                  {submitting ? 'Saving...' : 'Save All Terms'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Invoice Terms */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <Label className="font-semibold">Invoice Terms (GST Invoice)</Label>
                  </div>
                  {terms.invoice_terms.map((term, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-4">{index + 1}.</span>
                      <Input
                        value={term}
                        onChange={(e) => handleTermChange('invoice_terms', index, e.target.value)}
                        placeholder={`Term ${index + 1}`}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>

                {/* Cash Receipt Terms */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-purple-600" />
                    <Label className="font-semibold">Cash Receipt Terms (QCB)</Label>
                  </div>
                  {terms.cash_receipt_terms.map((term, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-4">{index + 1}.</span>
                      <Input
                        value={term}
                        onChange={(e) => handleTermChange('cash_receipt_terms', index, e.target.value)}
                        placeholder={`Term ${index + 1}`}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>

                {/* Estimation Terms */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <FileQuestion className="h-5 w-5 text-gray-600" />
                    <Label className="font-semibold">Estimation Terms (Quotation)</Label>
                  </div>
                  {terms.estimation_terms.map((term, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-4">{index + 1}.</span>
                      <Input
                        value={term}
                        onChange={(e) => handleTermChange('estimation_terms', index, e.target.value)}
                        placeholder={`Term ${index + 1}`}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-semibold text-blue-800 mb-2">Invoice Terms:</p>
                  <ol className="list-decimal pl-4 text-blue-700">
                    {terms.invoice_terms.filter(t => t.trim()).map((term, idx) => (
                      <li key={idx}>{term}</li>
                    ))}
                  </ol>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="font-semibold text-purple-800 mb-2">Cash Receipt Terms:</p>
                  <ol className="list-decimal pl-4 text-purple-700">
                    {terms.cash_receipt_terms.filter(t => t.trim()).map((term, idx) => (
                      <li key={idx}>{term}</li>
                    ))}
                  </ol>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-semibold text-gray-800 mb-2">Estimation Terms:</p>
                  <ol className="list-decimal pl-4 text-gray-700">
                    {terms.estimation_terms.filter(t => t.trim()).map((term, idx) => (
                      <li key={idx}>{term}</li>
                    ))}
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bank Account Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBank ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBank}>
            <div className="space-y-4">
              <div>
                <Label>Account Name *</Label>
                <Input
                  value={bankForm.account_name}
                  onChange={(e) => setBankForm(prev => ({ ...prev, account_name: e.target.value }))}
                  placeholder="e.g., SUNTECH ENTERPRISES"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Account Type *</Label>
                  <Select
                    value={bankForm.account_type}
                    onValueChange={(v) => setBankForm(prev => ({ ...prev, account_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Current">Current</SelectItem>
                      <SelectItem value="Corporate">Corporate</SelectItem>
                      <SelectItem value="Savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bank Name *</Label>
                  <Input
                    value={bankForm.bank_name}
                    onChange={(e) => setBankForm(prev => ({ ...prev, bank_name: e.target.value }))}
                    placeholder="e.g., HDFC Bank"
                  />
                </div>
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={bankForm.account_number}
                  onChange={(e) => setBankForm(prev => ({ ...prev, account_number: e.target.value }))}
                  placeholder="e.g., 50200012345678"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>IFSC Code *</Label>
                  <Input
                    value={bankForm.ifsc_code}
                    onChange={(e) => setBankForm(prev => ({ ...prev, ifsc_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., HDFC0001234"
                  />
                </div>
                <div>
                  <Label>Branch Name</Label>
                  <Input
                    value={bankForm.branch_name}
                    onChange={(e) => setBankForm(prev => ({ ...prev, branch_name: e.target.value }))}
                    placeholder="e.g., Main Branch"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={bankForm.is_primary}
                  onChange={(e) => setBankForm(prev => ({ ...prev, is_primary: e.target.checked }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="is_primary" className="cursor-pointer">Set as primary account (shown on invoices)</Label>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setBankDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : (editingBank ? 'Update' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingSettings;
